import type { Industry, IndustryCategory, IndustryCategoryInfo, IndustrySubcategory } from '@/types';
import { INDUSTRY_CATEGORIES } from '@/types';
import { simulate } from '@/services/api/client';
import { getDb } from '@/services/api/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as fbLimit,
  orderBy,
  query,
  where,
  type DocumentData,
  type QueryConstraint,
} from 'firebase/firestore';

/**
 * Industry discovery now reads from the Cloud Firestore `industries` collection
 * (migrated from the legacy RTDB export via scripts/migrate-industries-to-firestore.mjs).
 * Each document carries the NAICS hierarchy + `naicsCode` used for AI matching.
 *
 * Firestore is the source of truth; if a read fails or the collection is empty
 * (e.g. before the migration runs, or rules block the read) we fall back to the
 * small built-in sample so the UI never breaks. Set VITE_USE_FIRESTORE=false to
 * force the sample.
 */
const useFirestore = import.meta.env.VITE_USE_FIRESTORE !== 'false';
const COLLECTION = 'industries';
const CATEGORIES_COLLECTION = 'categories';
const DEFAULT_LIMIT = 250;
const ALL_LIMIT = 2000; // whole collection (~1,539 docs) for client-side search/filter

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

// Built-in sample — fallback only. Categories use the NAICS sector taxonomy.
const INDUSTRIES: Industry[] = [
  { id: 'ind_ai', name: 'AI Software Development', category: 'Information', description: 'Building next-generation artificial intelligence solutions for enterprise use cases.', growthRate: 25, marketSize: '$150B', competitionLevel: 'High', trending: true, location: 'Global' },
  { id: 'ind_renew', name: 'Renewable Energy Systems', category: 'Utilities', description: 'Sustainable power solutions including solar, wind, and energy storage.', growthRate: 18, marketSize: '$800B', competitionLevel: 'Medium', trending: true, location: 'North America' },
  { id: 'ind_telemed', name: 'Telemedicine Services', category: 'Health Care and Social Assistance', description: 'Remote healthcare delivery through digital platforms and connected devices.', growthRate: 15, marketSize: '$45B', competitionLevel: 'Medium', trending: false, location: 'Europe' },
  { id: 'ind_fintech', name: 'Fintech & Digital Banking', category: 'Finance and Insurance', description: 'Modernizing banking, payments, lending, and personal finance through technology.', growthRate: 22, marketSize: '$310B', competitionLevel: 'High', trending: true, location: 'Global' },
  { id: 'ind_agritech', name: 'AgriTech & Precision Farming', category: 'Agriculture, Forestry, Fishing and Hunting', description: 'Data-driven crop management, sensors, drones, and supply chain optimization.', growthRate: 12, marketSize: '$22B', competitionLevel: 'Low', trending: false, location: 'Asia Pacific' },
  { id: 'ind_edtech', name: 'EdTech Platforms', category: 'Educational Services', description: 'Digital learning, online cohorts, and AI tutoring at scale.', growthRate: 14, marketSize: '$90B', competitionLevel: 'High', trending: true, location: 'Global' },
  { id: 'ind_ecom', name: 'D2C E-commerce', category: 'Retail Trade', description: 'Direct-to-consumer brands powered by storefronts, social, and logistics tech.', growthRate: 10, marketSize: '$180B', competitionLevel: 'High', trending: false, location: 'Global' },
  { id: 'ind_smart_mfg', name: 'Smart Manufacturing', category: 'Manufacturing', description: 'IoT, robotics, and predictive maintenance for modern factories.', growthRate: 9, marketSize: '$240B', competitionLevel: 'Medium', trending: false, location: 'Asia Pacific' },
];

/**
 * Full offline fallback — the complete NAICS taxonomy (~1,539 industries) bundled
 * from the RTDB export via `scripts/migrate-industries-to-firestore.mjs --out=…`.
 * Lazily imported (its own chunk) only when Firestore is disabled/empty/unreadable,
 * so /industry/all and the details page keep working even if the `industries`
 * collection is wiped. Falls back to the tiny built-in sample if the chunk fails.
 */
let fallbackCache: Promise<Industry[]> | null = null;
function fullFallback(): Promise<Industry[]> {
  if (!fallbackCache) {
    fallbackCache = import('@/data/industries-fallback.json')
      .then((m) => (m.default as unknown as Industry[]))
      .catch(() => INDUSTRIES);
  }
  return fallbackCache;
}

/** Map a Firestore industry doc → the frontend `Industry` shape. */
function fromDoc(id: string, d: DocumentData): Industry {
  return {
    id: (d.id as string) ?? id,
    name: (d.name as string) ?? `NAICS ${d.naicsCode ?? id}`,
    category: (d.category as IndustryCategory) ?? 'Other Services (except Public Administration)',
    description: (d.description as string) ?? '',
    growthRate: typeof d.growthRate === 'number' ? d.growthRate : 0,
    marketSize: (d.marketSize as string) ?? '—',
    competitionLevel: (d.competitionLevel as Industry['competitionLevel']) ?? 'Medium',
    trending: Boolean(d.trending),
    location: (d.location as string) ?? 'Global',
    tags: Array.isArray(d.tags) ? (d.tags as string[]) : undefined,
    naicsCode: (d.naicsCode as string) ?? undefined,
    sectorId: (d.sectorId as string) ?? undefined,
    sectorName: (d.sectorName as string) ?? undefined,
    subsectorId: (d.subsectorId as string) ?? undefined,
    subsectorName: (d.subsectorName as string) ?? undefined,
    microSubsectorId: (d.microSubsectorId as string) ?? undefined,
    microSubsectorName: (d.microSubsectorName as string) ?? undefined,
    submicroSubsectorId: (d.submicroSubsectorId as string) ?? undefined,
  };
}

/** Run a Firestore query against `industries`, mapping + falling back to `mock`. */
async function fetchIndustries(
  constraints: QueryConstraint[],
  mock: () => Industry[],
): Promise<Industry[]> {
  if (!useFirestore) return simulate(mock());
  try {
    const snap = await getDocs(query(collection(getDb(), COLLECTION), ...constraints));
    if (snap.empty) return mock(); // not yet migrated → sample
    return snap.docs.map((s) => fromDoc(s.id, s.data()));
  } catch (err) {
    if (import.meta.env.DEV) console.warn('[endeeko] industries Firestore read failed — using sample.', err);
    return mock();
  }
}

// The industries collection is static reference data, so we load it once and
// cache it for the session. This powers multi-field search + category/subcategory
// filtering entirely client-side (Firestore can't do substring/multi-field search)
// and avoids any composite-index requirements.
let allCache: Promise<Industry[]> | null = null;
async function loadAll(): Promise<Industry[]> {
  if (useFirestore) {
    try {
      const snap = await getDocs(query(collection(getDb(), COLLECTION), fbLimit(ALL_LIMIT)));
      if (!snap.empty) return snap.docs.map((s) => fromDoc(s.id, s.data()));
      if (import.meta.env.DEV) console.warn('[endeeko] industries collection empty — using bundled fallback (run `npm run migrate:industries` to repopulate Firestore).');
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[endeeko] industries Firestore read failed — using bundled fallback.', err);
    }
  }
  return fullFallback();
}
function loadAllCached(): Promise<Industry[]> {
  if (!allCache) allCache = loadAll();
  return allCache;
}

export const industryService = {
  listAll: async (): Promise<Industry[]> => {
    const all = await loadAllCached();
    return [...all].sort((a, b) => a.name.localeCompare(b.name)).slice(0, DEFAULT_LIMIT);
  },

  listTrending: () =>
    fetchIndustries(
      [where('trending', '==', true), fbLimit(50)],
      () => INDUSTRIES.filter((i) => i.trending),
    ),

  listHighGrowth: () =>
    fetchIndustries(
      [orderBy('growthRate', 'desc'), fbLimit(5)],
      () => [...INDUSTRIES].sort((a, b) => b.growthRate - a.growthRate).slice(0, 5),
    ),

  // Category names — sourced from the live `categories` collection (falls back
  // to the static taxonomy). Used by filters that only need the names.
  listCategories: async (): Promise<IndustryCategory[]> =>
    (await industryService.listCategoryDetails()).map((c) => c.name),

  // Full category docs (name + sectorIds + accurate industryCount) from the
  // `categories` collection seeded by the migration. Falls back to the static
  // taxonomy (count 0) if Firestore is disabled, empty, or unreadable.
  listCategoryDetails: async (): Promise<IndustryCategoryInfo[]> => {
    // Derive per-category counts from the bundled fallback so the accordion shows
    // accurate numbers even when the `categories` collection is empty/wiped.
    const fallback = async (): Promise<IndustryCategoryInfo[]> => {
      const all = await fullFallback();
      const counts = new Map<string, number>();
      for (const i of all) counts.set(i.category, (counts.get(i.category) ?? 0) + 1);
      return INDUSTRY_CATEGORIES.map((name) => ({ id: slug(name), name, sectorIds: [], industryCount: counts.get(name) ?? 0 }));
    };
    if (!useFirestore) return fallback();
    try {
      const snap = await getDocs(query(collection(getDb(), CATEGORIES_COLLECTION), orderBy('name')));
      if (snap.empty) return fallback();
      return snap.docs.map((s) => {
        const d = s.data();
        return {
          id: (d.id as string) ?? s.id,
          name: d.name as IndustryCategory,
          sectorIds: Array.isArray(d.sectorIds) ? (d.sectorIds as string[]) : [],
          industryCount: typeof d.industryCount === 'number' ? d.industryCount : 0,
        };
      });
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[endeeko] categories Firestore read failed — using static taxonomy.', err);
      return fallback();
    }
  },

  // Filtered from the cached full list (no composite index needed); sorted by
  // sub-category then industry name.
  listByCategory: async (category: IndustryCategory): Promise<Industry[]> => {
    const all = await loadAllCached();
    return all
      .filter((i) => i.category === category)
      .sort(
        (a, b) =>
          (a.subsectorName ?? '').localeCompare(b.subsectorName ?? '') || a.name.localeCompare(b.name),
      );
  },

  // Group a category's industries by sub-category (NAICS subsector) so the UI
  // can show: main category → all sub-categories → industries within each.
  listSubcategories: async (category: IndustryCategory): Promise<IndustrySubcategory[]> => {
    const industries = await industryService.listByCategory(category);
    const groups = new Map<string, IndustrySubcategory>();
    for (const ind of industries) {
      const name = ind.subsectorName || 'Other';
      const key = ind.subsectorId || name;
      const group = groups.get(key) ?? { subsectorId: ind.subsectorId ?? '', subsectorName: name, industries: [] };
      group.industries.push(ind);
      groups.set(key, group);
    }
    return [...groups.values()].sort((a, b) => a.subsectorName.localeCompare(b.subsectorName));
  },

  listByLocation: (location: string) =>
    fetchIndustries(
      [where('location', '==', location), fbLimit(DEFAULT_LIMIT)],
      () => INDUSTRIES.filter((i) => i.location.toLowerCase() === location.toLowerCase() || i.location === 'Global'),
    ),

  // Multi-field substring search over the cached list: industry name, NAICS code,
  // category (sector), sub-category (subsector), and description.
  search: async (queryStr: string): Promise<Industry[]> => {
    const q = queryStr.trim().toLowerCase();
    const all = await loadAllCached();
    const sorted = (list: Industry[]) =>
      [...list].sort((a, b) => a.name.localeCompare(b.name)).slice(0, DEFAULT_LIMIT);
    if (!q) return sorted(all);
    return sorted(
      all.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          (i.naicsCode ?? '').toLowerCase().includes(q) ||
          i.category.toLowerCase().includes(q) ||
          (i.subsectorName ?? '').toLowerCase().includes(q) ||
          i.description.toLowerCase().includes(q),
      ),
    );
  },

  getById: async (id: string): Promise<Industry | null> => {
    const fallback = async () => (await fullFallback()).find((i) => i.id === id) ?? null;
    if (!useFirestore) return fallback();
    try {
      const snap = await getDoc(doc(getDb(), COLLECTION, id));
      if (snap.exists()) return fromDoc(snap.id, snap.data());
      return fallback();
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[endeeko] getIndustry Firestore read failed — using bundled fallback.', err);
      return fallback();
    }
  },

  compare: async (ids: string[]): Promise<Industry[]> => {
    const results = await Promise.all(ids.map((id) => industryService.getById(id)));
    return results.filter((i): i is Industry => i !== null);
  },
};
